/* eslint-disable no-param-reassign */
import { Size, SizeType, SizeValue } from 'craft/components/Size';
import { isDomElement } from 'craft/utils/viewport';

import { Axes, DraggingStart, Movement } from './types';

export const px = (str: number) => `${str}px`;

export const resetDomSizes = (dom: HTMLElement) => {
  if (!isDomElement(dom)) {
    return;
  }
  dom.style.width = '';
  dom.style.height = '';
};

export const updateDomSizes = (
  dom: HTMLElement,
  axis: Axes,
  width: number,
  height: number,
  size: Size,
) => {
  if (!isDomElement(dom)) {
    return;
  }
  const updateWidth = () => {
    dom.style.width = px(width);
  };
  const updateHeight = () => {
    if (size.height.type === SizeType.Percentage) {
      return;
    }
    dom.style.height = px(height);
  };
  if (axis === Axes.All) {
    updateWidth();
    updateHeight();
  } else if (axis === Axes.X) {
    updateWidth();
  } else if (axis === Axes.Y) {
    updateHeight();
  }
};

export const calculateNewSize = (
  { clientX, clientY }: { clientX: number; clientY: number },
  draggingStart: DraggingStart,
) => {
  const {
    startingX,
    startingY,
    startingWidth,
    startingHeight,
    movement,
    resizeHandle,
  } = draggingStart;

  const movementCoefficient = movement === Movement.Inverted ? -1 : 1;
  let newWidth: number;
  if (resizeHandle === 'sw') {
    // the bottom-left resize handle specifically needs to do an inverted calculation only on the x-axis in order to work properly
    newWidth = startingWidth + (clientX - startingX) * -movementCoefficient;
  } else {
    newWidth = startingWidth + (clientX - startingX) * movementCoefficient;
  }
  const newHeight = startingHeight + (clientY - startingY) * movementCoefficient;
  return { newWidth, newHeight };
};

const computePercentage = (
  latestValuePx = 0,
  originalValuePx = 0,
  originalPercentage: SizeValue = SizeType.Default,
  parentWidth: number = originalValuePx,
) => {
  if (originalValuePx === 0) {
    return 0;
  }

  if (originalPercentage === SizeType.Default) {
    return Math.round((100 * latestValuePx) / parentWidth);
  }

  return Math.round((latestValuePx * originalPercentage) / originalValuePx);
};

export const convertPixelsToSize = (
  // the new, latest incoming size
  latestSizePx: { width?: number; height?: number },
  originalSizePx: { width?: number; height?: number },
  // the original size of the element that's being computed
  size: Size,
  parentWidth?: number,
  allowPercentages = true,
): Size => {
  if (!latestSizePx.width && !latestSizePx.height) {
    return size;
  }

  let sizeType = size.width.type;
  // if we're dragging horizontally, we should go to %
  // this is particularly useful for images since we want to make sure that they stay responsive
  if (size.width.type === SizeType.Default || size.width.type === SizeType.Auto) {
    if (allowPercentages && typeof latestSizePx.height === 'undefined') {
      sizeType = SizeType.Percentage;
    } else if (allowPercentages && latestSizePx.height === originalSizePx.height) {
      sizeType = SizeType.Percentage;
    } else {
      sizeType = SizeType.Pixel;
    }
  }

  if (sizeType === SizeType.Pixel) {
    return {
      width: { value: latestSizePx.width, type: SizeType.Pixel },
      height: { value: latestSizePx.height, type: SizeType.Pixel },
    };
  }
  return {
    width: {
      value: computePercentage(
        latestSizePx.width,
        originalSizePx.width,
        size.width.value,
        parentWidth,
      ),
      type: SizeType.Percentage,
    },
    height: {
      value: computePercentage(latestSizePx.height, originalSizePx.height, size.height.value),
      type: SizeType.Percentage,
    },
  };
};
